import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Manufacturing Module Service Stores
 */
export const manufacturingStores: ServiceStoreConfig[] = [
  // Manhole Designer module stores
  {
    name: "manhole-calculator",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  {
    name: "manhole-components",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  // NEW: manhole-bom service - links manhole components to BOM components
  // Added as part of Dec 2025 migration (see FRONTEND_MIGRATION_GUIDE_manhole_components.md)
  {
    name: "manhole-bom",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "manhole-pipe-connections",
    options: { enableStorage: true, enableErrorNotifications: false },
  },
  // Manhole Pipes - pipe type definitions (PVC, RCP, etc.) for reuse across projects
  {
    name: "manhole-pipes",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "manhole-projects",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "manhole-quotes",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.manholeQuotesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalQuotes = entities.length;

          const totalValue = entities.reduce(
            (sum: number, quote: Record<string, unknown>) =>
              sum + (typeof quote.total === "number" ? quote.total : 0),
            0
          );

          const avgValue = totalQuotes > 0 ? totalValue / totalQuotes : 0;

          const uniqueCustomers = new Set(
            entities
              .map(
                (quote: Record<string, unknown>) => quote.customerName
              )
              .filter(
                (name: unknown) => name !== undefined && name !== null
              )
          ).size;

          return [
            {
              title: "Total Quotes",
              value: () => totalQuotes.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Value",
              value: () => `$${totalValue.toFixed(2)}`,
              valueColor: "success.main",
            },
            {
              title: "Avg. Quote Value",
              value: () => `$${avgValue.toFixed(2)}`,
              valueColor: "info.main",
            },
            {
              title: "Unique Customers",
              value: () => uniqueCustomers.toString(),
              valueColor: "secondary.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "manhole-quote-items",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "manhole-quote-calculator",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  // manhole-quote-converter: Converts manhole quotes to sales orders via POST
  {
    name: "manhole-quote-converter",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  {
    name: "manhole-pdf",
    options: { enableStorage: false, enableErrorNotifications: true },
  },
  // NEW: Additional manhole-related stores for the remaining 10%
  {
    name: "manhole-castings",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "castingId",
    },
  },
  {
    name: "manhole-validations",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      idField: "validationId",
    },
  },
  {
    name: "manhole-forms",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "formId",
    },
  },
  {
    name: "manhole-hatches",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "hatchId",
    },
  },
  {
    name: "manhole-sequences",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // NEW: Manhole structures service (per-structure data within a project)
  {
    name: "manhole-structures",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // NEW: Manhole internals service (steps, ladder, options per structure)
  {
    name: "manhole-internals",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // Structure Codes - user-defined component groupings for manhole configurations
  {
    name: "structure-codes",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // Structure Code Components - links structure codes to manhole components
  {
    name: "structure-code-components",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // Plant Products - links plants to manhole components (product availability per plant)
  {
    name: "plant-products",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // Work Orders - individual piece-level scheduling (standalone or linked to sales orders)
  {
    name: "work-orders",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  // New Manufacturing Services
  {
    name: "products",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProducts = entities.length;

          const totalEstimatedCost = entities.reduce(
            (sum: number, product: Record<string, unknown>) =>
              sum +
              (typeof product.estimatedCost === "number"
                ? product.estimatedCost
                : 0),
            0
          );

          const avgCost =
            totalProducts > 0
              ? (totalEstimatedCost as number) / totalProducts
              : 0;

          const departments = new Set(
            entities.map(
              (product: Record<string, unknown>) => product.departmentId
            )
          ).size;

          return [
            {
              title: "Total Products",
              value: () => totalProducts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Avg. Estimated Cost",
              value: () => `$${avgCost.toFixed(2)}`,
              valueColor: "info.main",
            },
            {
              title: "Total Estimated Cost",
              value: () => `$${(totalEstimatedCost as number).toFixed(2)}`,
              valueColor: "success.main",
            },
            {
              title: "Departments",
              value: () => departments.toString(),
              valueColor: "secondary.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "bom-components",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.bomComponentsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalComponents = entities.length;

          const categories = new Set(
            entities
              .map((component: Record<string, unknown>) => component.category)
              .filter((cat: unknown) => cat !== undefined && cat !== null)
          ).size;

          const totalUnitCost = entities.reduce(
            (sum: number, component: Record<string, unknown>) =>
              sum +
              (typeof component.unitCost === "number" ? component.unitCost : 0),
            0
          );
          const avgUnitCost =
            (totalUnitCost as number) / (totalComponents || 1);

          const highValueComponents = entities.filter(
            (component: Record<string, unknown>) =>
              typeof component.unitCost === "number" && component.unitCost > 100
          ).length;

          return [
            {
              title: "Total Components",
              value: () => totalComponents.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Categories",
              value: () => categories.toString(),
              valueColor: "secondary.main",
            },
            {
              title: "Avg. Unit Cost",
              value: () => `$${avgUnitCost.toFixed(2)}`,
              valueColor: "info.main",
            },
            {
              title: "High Value (>$100)",
              value: () => highValueComponents.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },

  {
    name: "forms",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.formsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalForms = entities.length;

          const departments = new Set(
            entities.map((form: Record<string, unknown>) => form.departmentId)
          ).size;

          return [
            {
              title: "Total Forms",
              value: () => totalForms.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Departments",
              value: () => departments.toString(),
              valueColor: "secondary.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "labor-types",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.laborTypesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalLaborTypes = entities.length;

          const ratesWithValues = entities.filter(
            (labor: Record<string, unknown>) =>
              typeof labor.hourlyRate === "number"
          );

          const totalHourlyRate = ratesWithValues.reduce(
            (sum: number, labor: Record<string, unknown>) =>
              sum +
              (typeof labor.hourlyRate === "number" ? labor.hourlyRate : 0),
            0
          );
          const avgHourlyRate =
            (totalHourlyRate as number) / (ratesWithValues.length || 1);

          const maxRate = Math.max(
            0,
            ...entities.map((labor: Record<string, unknown>) =>
              typeof labor.hourlyRate === "number" ? labor.hourlyRate : 0
            )
          );

          const minRate = Math.min(
            Infinity,
            ...entities
              .filter(
                (labor: Record<string, unknown>) =>
                  typeof labor.hourlyRate === "number"
              )
              .map(
                (labor: Record<string, unknown>) => labor.hourlyRate as number
              )
          );

          return [
            {
              title: "Total Labor Types",
              value: () => totalLaborTypes.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Avg. Hourly Rate",
              value: () => `$${avgHourlyRate.toFixed(2)}`,
              valueColor: "info.main",
            },
            {
              title: "Highest Rate",
              value: () => (maxRate > 0 ? `$${maxRate.toFixed(2)}` : "N/A"),
              valueColor: "success.main",
            },
            {
              title: "Lowest Rate",
              value: () =>
                minRate !== Infinity ? `$${minRate.toFixed(2)}` : "N/A",
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "product-bom",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productBomStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalBomItems = entities.length;

          const uniqueProducts = new Set(
            entities.map((item: Record<string, unknown>) => item.productId)
          ).size;

          const uniqueComponents = new Set(
            entities.map((item: Record<string, unknown>) => item.componentId)
          ).size;

          const totalQuantity = entities.reduce(
            (sum: number, item: Record<string, unknown>) =>
              sum + (typeof item.quantity === "number" ? item.quantity : 0),
            0
          );

          return [
            {
              title: "Total BOM Items",
              value: () => totalBomItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Products",
              value: () => uniqueProducts.toString(),
              valueColor: "secondary.main",
            },
            {
              title: "Components Used",
              value: () => uniqueComponents.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Quantity",
              value: () => totalQuantity.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "product-labor",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productLaborStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalLaborAssignments = entities.length;

          const totalHours = entities.reduce(
            (sum: number, labor: Record<string, unknown>) =>
              sum +
              (typeof labor.hoursPerUnit === "number" ? labor.hoursPerUnit : 0),
            0
          );

          const totalCost = entities.reduce(
            (sum: number, labor: Record<string, unknown>) =>
              sum +
              (typeof labor.costPerUnit === "number" ? labor.costPerUnit : 0),
            0
          );

          const uniqueProducts = new Set(
            entities.map((labor: Record<string, unknown>) => labor.productId)
          ).size;

          return [
            {
              title: "Labor Assignments",
              value: () => totalLaborAssignments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Products",
              value: () => uniqueProducts.toString(),
              valueColor: "secondary.main",
            },
            {
              title: "Total Hours/Unit",
              value: () => (totalHours as number).toFixed(2),
              valueColor: "info.main",
            },
            {
              title: "Total Cost/Unit",
              value: () => `$${(totalCost as number).toFixed(2)}`,
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
